import React, { useState } from "react";

interface VideoFeedProps {
  title?: string;
  description: string;
}

export const VideoFeed: React.FC<VideoFeedProps> = ({ title, description }) => {
  const [isDemoStarted, setIsDemoStarted] = useState(false);
  const [isFrameLoading, setIsFrameLoading] = useState(true);

  const startDemo = () => {
    setIsFrameLoading(true);
    setIsDemoStarted(true);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-24 sm:px-6 lg:px-8">
      <div className="relative w-full max-w-6xl">
        <div className="absolute left-2 top-2 h-full w-full rounded-2xl bg-black" />
        <div className="relative rounded-2xl border-2 border-black bg-white p-5 sm:p-8">
          <div className="grid gap-8 lg:grid-cols-[minmax(240px,320px)_1fr] lg:items-center">
            <div className="space-y-5">
              <div>
                <p className="mb-2 font-arvo text-xs font-bold uppercase tracking-[0.24em] text-red-600">
                  Camera permission required
                </p>
                <h2 className="font-cinzel text-4xl italic leading-tight text-black sm:text-5xl">
                  {title}
                </h2>
              </div>

              <p className="font-spectral text-lg italic leading-relaxed text-neutral-800">
                {description}
              </p>

              <div className="space-y-3 border-l-4 border-red-600 pl-4 font-spectral text-base leading-relaxed text-neutral-700">
                <p>
                  Your browser will ask before the webcam turns on. Start the
                  demo only when you are ready and have room to move safely.
                </p>
                <p>
                  The embedded camera session runs at cam.powerkids.vn for this
                  prototype. sifu.art does not record or store your video from
                  this page.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <button
                  type="button"
                  onClick={startDemo}
                  className="rounded-md border-2 border-black bg-red-600 px-5 py-3 font-cinzel text-sm font-bold uppercase tracking-wider text-white shadow-[5px_5px_0_#000] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-200"
                >
                  {isDemoStarted ? "Camera Demo Ready" : "Start Camera Demo"}
                </button>
                <a
                  href="https://cam.powerkids.vn"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md border-2 border-red-600 bg-white px-5 py-3 text-center font-cinzel text-sm font-bold uppercase tracking-wider text-red-600 transition-colors duration-200 hover:bg-red-50"
                >
                  Open in New Tab
                </a>
              </div>
            </div>

            <div className="min-h-[420px] overflow-hidden rounded-lg border-2 border-black bg-neutral-950">
              {isDemoStarted ? (
                <div className="relative">
                  {isFrameLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white p-6 text-center font-spectral text-sm italic leading-6 text-black/70">
                      Loading the camera demo. If the frame stays blank,
                      refresh the page and check camera permissions.
                    </div>
                  )}
                  <iframe
                    src="https://cam.powerkids.vn"
                    title="Staff form camera demo"
                    className="h-[520px] w-full lg:h-[640px]"
                    allow="camera; microphone; autoplay; encrypted-media; fullscreen"
                    sandbox="allow-scripts allow-same-origin"
                    onLoad={() => setIsFrameLoading(false)}
                  />
                </div>
              ) : (
                <div className="flex h-[420px] flex-col items-center justify-center px-6 text-center lg:h-[640px]">
                  <div className="mb-6 h-24 w-24 rounded-full border-2 border-red-500 bg-[radial-gradient(circle_at_center,#dc2626_0,#dc2626_18%,transparent_19%),linear-gradient(90deg,transparent_48%,#ffffff_49%,#ffffff_51%,transparent_52%),linear-gradient(0deg,transparent_48%,#ffffff_49%,#ffffff_51%,transparent_52%)] opacity-90" />
                  <p className="max-w-md font-cinzel text-2xl font-bold uppercase tracking-wider text-white">
                    Preview paused
                  </p>
                  <p className="mt-3 max-w-md font-spectral text-base leading-relaxed text-neutral-300">
                    The camera demo stays unloaded until you choose to start it.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
